import React, { useState } from "react";
import { IResourceComponentsProps, useApiUrl, useCustom, useExport } from "@refinedev/core";
import { List, ShowButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Flex,
  Group,
  LoadingOverlay,
  Pagination,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Grid,
  Loader,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Link, useSearchParams } from "react-router-dom";
import {
  IconList,
  IconX,
  IconShieldCheck,
  IconEye,
  IconAlertTriangle,
  IconColumns,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";
import { CancelPerson } from "entities/cancel_flow";
import { selectBrands, colorsByBrand } from "entities/cancel_flow/brand";
import { selectOutcomes, colorsByOutcome } from "entities/cancel_flow/outcome";
import { colorsByOfferType } from "entities/cancel_flow/offerType";
import { DateFilter } from "components/common/filters/DateFilter";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { StatCard } from "./StatCard";

const TABS = [
  { value: "all", label: "All", icon: <IconList size={16} /> },
  { value: "cancels", label: "Cancels", icon: <IconX size={16} /> },
  { value: "saves", label: "Saves", icon: <IconShieldCheck size={16} /> },
  { value: "watchlist", label: "Watchlist", icon: <IconEye size={16} /> },
  {
    value: "lost_deflects",
    label: "Lost Deflects",
    icon: <IconAlertTriangle size={16} />,
  },
];

type SummaryData = {
  total: number;
  cancelled: number;
  deflected: number;
  abandoned: number;
  in_progress: number;
  deflection_rate: number;
  date_from: string;
  date_to: string;
};

type WatchlistSummaryData = {
  watching: number;
  saved: number;
  lost: number;
  overdue: number;
};

type ReasonBreakdownItem = {
  reason: string;
  count: number;
};

const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  id: true,
  user: true,
  user_name: false,
  brand: true,
  plan_segment: false,
  page_variant: false,
  outcome: true,
  subscription_mrr: true,
  customer_country: false,
  cancel_reason: true,
  offer_result: false,
  competitor: false,
  return_likelihood: true,
  watchlist_status: true,
  started_at: true,
  completed_at: false,
  chargebee_customer_id: false,
  chargebee_subscription_id: false,
  chargebee_plan_id: false,
  actions: true,
};

export const CancelPeopleList: React.FC<IResourceComponentsProps> = () => {
  const apiUrl = useApiUrl();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get("tab") ?? "all"
  );
  const [brand, setBrand] = useState<string | null>(searchParams.get("brand") ?? null);
  const [dateFrom, setDateFrom] = useState<Date | null>(
    searchParams.get("date_from") ? new Date(searchParams.get("date_from")!) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [dateTo, setDateTo] = useState<Date | null>(
    searchParams.get("date_to") ? new Date(searchParams.get("date_to")!) : new Date()
  );

  const endOfDay = (d: Date) => {
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const queryParams: Record<string, string> = {};
  if (brand) queryParams.brand = brand;
  if (dateFrom) queryParams.date_from = dateFrom.toISOString();
  if (dateTo) queryParams.date_to = endOfDay(dateTo).toISOString();

  const needsSummary = ["all", "cancels", "saves"].includes(activeTab);
  const needsWatchlist = ["all", "watchlist"].includes(activeTab);
  const needsReasons = activeTab === "cancels";

  const { data: summaryData } = useCustom<SummaryData>({
    url: `${apiUrl}/cancel_dashboard/summary`,
    method: "get",
    config: { query: queryParams },
    queryOptions: { enabled: needsSummary },
  });

  const { data: watchlistData } = useCustom<WatchlistSummaryData>({
    url: `${apiUrl}/cancel_dashboard/watchlist_summary`,
    method: "get",
    config: { query: queryParams },
    queryOptions: { enabled: needsWatchlist },
  });

  const { data: reasonsData } = useCustom<ReasonBreakdownItem[]>({
    url: `${apiUrl}/cancel_dashboard/reasons_breakdown`,
    method: "get",
    config: { query: queryParams },
    queryOptions: { enabled: needsReasons },
  });

  const summary = summaryData?.data;
  const watchlist = watchlistData?.data;
  const reasons = reasonsData?.data as unknown as ReasonBreakdownItem[] | undefined;
  const topReason = reasons && reasons.length > 0 ? reasons[0] : null;
  const columns = React.useMemo<ColumnDef<CancelPerson>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
      },
      {
        id: "user",
        header: "User",
        cell: ({ row }) => {
          const user = row.original.user;
          if (!user) return <Text color="dimmed">—</Text>;
          return (
            <Anchor component={Link} to={`/users/show/${user.id}`} size="sm">
              {user.email}
            </Anchor>
          );
        },
        meta: { isPlaceholder: true, disableSort: true },
      },
      {
        id: "user_name",
        header: "Name",
        cell: ({ row }) => {
          const user = row.original.user;
          if (!user) return <Text color="dimmed">—</Text>;
          return (
            <Text size="sm" style={{ whiteSpace: "nowrap" }}>
              {user.first_name} {user.last_name}
            </Text>
          );
        },
        meta: { isPlaceholder: true, disableSort: true },
      },
      {
        id: "brand",
        accessorKey: "brand",
        header: "Brand",
        cell: ({ getValue }) => {
          const value = getValue<CancelPerson["brand"]>();
          return (
            <Badge variant="filled" color={colorsByBrand[value]}>
              {value}
            </Badge>
          );
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<CancelPerson, any>;
          }) => (
            <Select
              placeholder="Brand"
              data={selectBrands}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "plan_segment",
        accessorKey: "plan_segment",
        header: "Segment",
      },
      {
        id: "page_variant",
        accessorKey: "page_variant",
        header: "Variant",
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          if (!value) return <Text color="dimmed">—</Text>;
          return <Badge variant="outline">{value}</Badge>;
        },
      },
      {
        id: "outcome",
        accessorKey: "outcome",
        header: "Outcome",
        cell: ({ getValue }) => {
          const value = getValue<CancelPerson["outcome"]>();
          if (!value) return <Badge color="gray">Pending</Badge>;
          return (
            <Badge variant="filled" color={colorsByOutcome[value]}>
              {value}
            </Badge>
          );
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<CancelPerson, any>;
          }) => (
            <Select
              placeholder="Outcome"
              data={selectOutcomes}
              onChange={header.column.setFilterValue}
              clearable
            />
          ),
        },
      },
      {
        id: "subscription_mrr",
        accessorKey: "subscription_mrr",
        header: "MRR",
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? `$${value}` : "—";
        },
      },
      {
        id: "customer_country",
        accessorKey: "customer_country",
        header: "Country",
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value || "—";
        },
      },
      {
        id: "cancel_reason",
        header: "Cancel Reason",
        cell: ({ row }) => {
          const reason =
            row.original.cancel_survey_response?.cancellation_reason;
          return reason ? (
            <Text size="sm">{reason.label}</Text>
          ) : (
            <Text color="dimmed">—</Text>
          );
        },
        meta: { isPlaceholder: true, disableSort: true },
      },
      {
        id: "offer_result",
        header: "Offer Result",
        cell: ({ row }) => {
          const interactions = row.original.offer_interactions;
          if (!interactions || interactions.length === 0)
            return <Text color="dimmed">—</Text>;
          const accepted = interactions.find((i) => i.accepted);
          if (accepted) {
            return (
              <Badge
                color={colorsByOfferType[accepted.offer_type] || "green"}
              >
                Accepted ({accepted.offer_type})
              </Badge>
            );
          }
          return <Badge color="gray">Declined</Badge>;
        },
        meta: { isPlaceholder: true, disableSort: true },
      },
      {
        id: "competitor",
        header: "Competitor",
        cell: ({ row }) => {
          const value = row.original.cancel_survey_response?.competitor;
          return value ? (
            <Text size="sm">{value}</Text>
          ) : (
            <Text color="dimmed">—</Text>
          );
        },
        meta: { isPlaceholder: true, disableSort: true },
      },
      {
        id: "return_likelihood",
        header: "Return",
        cell: ({ row }) => {
          const value =
            row.original.cancel_survey_response?.return_likelihood;
          if (value == null) return <Text color="dimmed">—</Text>;
          const color = value >= 7 ? "green" : value >= 4 ? "yellow" : "red";
          return (
            <Badge variant="filled" color={color}>
              {value}/10
            </Badge>
          );
        },
        meta: { isPlaceholder: true, disableSort: true },
      },
      {
        id: "watchlist_status",
        header: "Watchlist",
        cell: ({ row }) => {
          const entry = row.original.watchlist_entry;
          if (!entry) return <Text color="dimmed">—</Text>;
          const colorMap: Record<string, string> = {
            watching: "blue",
            saved: "green",
            lost: "red",
            overdue: "orange",
          };
          return (
            <Badge color={colorMap[entry.status] || "gray"}>
              {entry.status}
            </Badge>
          );
        },
        meta: { isPlaceholder: true, disableSort: true },
      },
      {
        id: "started_at",
        accessorKey: "started_at",
        header: "Started At",
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return value ? new Date(value).toLocaleString() : "";
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<CancelPerson, any>;
          }) => <DateFilter header={header} />,
          disableSort: true,
        },
      },
      {
        id: "completed_at",
        accessorKey: "completed_at",
        header: "Completed At",
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return value ? new Date(value).toLocaleString() : "";
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<CancelPerson, any>;
          }) => <DateFilter header={header} />,
          disableSort: true,
        },
      },
      {
        id: "chargebee_customer_id",
        accessorKey: "chargebee_customer_id",
        header: "Customer ID",
      },
      {
        id: "chargebee_subscription_id",
        accessorKey: "chargebee_subscription_id",
        header: "Subscription ID",
      },
      {
        id: "chargebee_plan_id",
        accessorKey: "chargebee_plan_id",
        header: "Plan ID",
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row: { original } }) {
          return (
            <Flex columnGap={4}>
              <ShowButton hideText recordItemId={original.id} />
            </Flex>
          );
        },
        meta: { isPlaceholder: true },
      },
    ],
    []
  );

  const {
    getHeaderGroups,
    getRowModel,
    getFlatHeaders,
    getAllLeafColumns,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { isFetching, refetch },
    },
  } = useTable({
    columns,
    initialState: {
      columnVisibility: DEFAULT_COLUMN_VISIBILITY,
    },
    refineCoreProps: {
      resource: "cancel_people",
      pagination: { pageSize: 50 },
      sorters: {
        initial: [{ field: "started_at", order: "desc" }],
      },
      filters: {
        permanent: [
          { field: "tab", operator: "eq", value: activeTab },
          ...(brand ? [{ field: "brand", operator: "eq" as const, value: brand }] : []),
          ...(dateFrom ? [{ field: "date_from", operator: "eq" as const, value: dateFrom.toISOString() }] : []),
          ...(dateTo ? [{ field: "date_to", operator: "eq" as const, value: endOfDay(dateTo).toISOString() }] : []),
        ],
      },
    },
  });

  const exportFilters = [
    { field: "tab", operator: "eq" as const, value: activeTab },
    ...(brand ? [{ field: "brand", operator: "eq" as const, value: brand }] : []),
    ...(dateFrom ? [{ field: "date_from", operator: "eq" as const, value: dateFrom.toISOString() }] : []),
    ...(dateTo ? [{ field: "date_to", operator: "eq" as const, value: endOfDay(dateTo).toISOString() }] : []),
  ];

  const { triggerExport, isLoading: exportLoading } = useExport<CancelPerson>({
    resource: "cancel_people",
    filters: exportFilters,
    mapData: (person) => ({
      ID: person.id,
      Email: person.user?.email ?? "",
      Name: person.user
        ? [person.user.first_name, person.user.last_name].filter(Boolean).join(" ")
        : "",
      Brand: person.brand,
      Segment: person.plan_segment,
      Variant: person.page_variant ?? "",
      Outcome: person.outcome ?? "",
      MRR: person.subscription_mrr ?? "",
      Country: person.customer_country ?? "",
      "Cancel Reason":
        person.cancel_survey_response?.cancellation_reason?.label ?? "",
      "Offer Result": (() => {
        const interactions = person.offer_interactions;
        if (!interactions || interactions.length === 0) return "";
        const accepted = interactions.find((i) => i.accepted);
        if (accepted) return `Accepted (${accepted.offer_type})`;
        return "Declined";
      })(),
      Competitor: person.cancel_survey_response?.competitor ?? "",
      "Return Likelihood":
        person.cancel_survey_response?.return_likelihood ?? "",
      "Watchlist Status": person.watchlist_entry?.status ?? "",
      "Started At": person.started_at,
      "Completed At": person.completed_at ?? "",
      "Customer ID": person.chargebee_customer_id,
      "Subscription ID": person.chargebee_subscription_id,
      "Plan ID": person.chargebee_plan_id,
    }),
  });

  const handleTabChange = (value: string | null) => {
    setActiveTab(value ?? "all");
    setCurrent(1);
  };

  return (
    <List canCreate={false}>
      <Group position="apart" mb="md">
        <Tabs value={activeTab} onTabChange={handleTabChange}>
          <Tabs.List>
            {TABS.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value} icon={tab.icon}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        <Group spacing="xs">
          <Select
            placeholder="All Brands"
            data={[{ value: "", label: "All Brands" }, ...selectBrands]}
            value={brand}
            onChange={setBrand}
            clearable
            sx={{ width: 180 }}
          />
          <DatePicker
            placeholder="From"
            value={dateFrom}
            onChange={setDateFrom}
            clearable
            sx={{ width: 160 }}
          />
          <DatePicker
            placeholder="To"
            value={dateTo}
            onChange={setDateTo}
            clearable
            sx={{ width: 160 }}
          />
          <ActionIcon variant="subtle" onClick={() => refetch()} loading={isFetching}>
            <IconRefresh size={18} />
          </ActionIcon>
          <Button
            variant="subtle"
            leftIcon={<IconDownload size={16} />}
            compact
            loading={exportLoading}
            onClick={triggerExport}
          >
            Export CSV
          </Button>
          <Popover width={250} position="bottom-end" shadow="md">
          <Popover.Target>
            <Button
              variant="subtle"
              leftIcon={<IconColumns size={16} />}
              compact
            >
              Columns
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack spacing="xs">
              {getAllLeafColumns()
                .filter((column) => column.id !== "actions")
                .map((column) => (
                  <Checkbox
                    key={column.id}
                    label={
                      typeof column.columnDef.header === "string"
                        ? column.columnDef.header
                        : column.id
                    }
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                    size="xs"
                  />
                ))}
            </Stack>
          </Popover.Dropdown>
          </Popover>
        </Group>
      </Group>

      {activeTab === "all" && summary && watchlist && (
        <Grid mb="md">
          <Grid.Col span={4}>
            <StatCard title="Saved Customers" value={summary.deflected} color="green" />
          </Grid.Col>
          <Grid.Col span={4}>
            <StatCard title="Active Watchlist" value={watchlist.watching} color="blue" />
          </Grid.Col>
          <Grid.Col span={4}>
            <StatCard title="Canceled Customers" value={summary.cancelled} color="red" />
          </Grid.Col>
        </Grid>
      )}

      {activeTab === "cancels" && summary && (
        <Grid mb="md">
          <Grid.Col span={6}>
            <StatCard title="Canceled Customers" value={summary.cancelled} color="red" />
          </Grid.Col>
          <Grid.Col span={6}>
            <StatCard
              title="Top Cancel Reason"
              value={topReason?.reason ?? "—"}
              color="orange"
              subtitle={topReason ? `${topReason.count} occurrences` : undefined}
            />
          </Grid.Col>
        </Grid>
      )}

      {activeTab === "saves" && summary && (
        <Grid mb="md">
          <Grid.Col span={4}>
            <StatCard title="Saved Customers" value={summary.deflected} color="green" />
          </Grid.Col>
        </Grid>
      )}

      {activeTab === "watchlist" && watchlist && (
        <Grid mb="md">
          <Grid.Col span={4}>
            <StatCard title="Active Watchlist" value={watchlist.watching} color="blue" />
          </Grid.Col>
        </Grid>
      )}

      <AppliedFilters<CancelPerson> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<CancelPerson> headerGroups={getHeaderGroups()} />
          <Rows<CancelPerson> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
